import moment from "moment";

import { IdentityAuditEvent } from "@/queries/identity/useIdentityAuditEvents";

export type AuditTableRowType = "event" | "groupHeader";

export interface AuditEventTableRow extends IdentityAuditEvent {
  rowType: "event";
}

export interface AuditGroupHeaderRow {
  id: string;
  rowType: "groupHeader";
  label: string;
}

export type AuditTableRow = AuditEventTableRow | AuditGroupHeaderRow;

export const isAuditGroupHeaderRow = (row: AuditTableRow): row is AuditGroupHeaderRow =>
  row.rowType === "groupHeader";

export const isAuditEventTableRow = (row: AuditTableRow): row is AuditEventTableRow =>
  row.rowType === "event";

export const formatGroupLabel = (date: moment.Moment): string => {
  const today = moment().startOf("day");
  if (date.isSame(today, "day")) {
    return "Today";
  }
  if (date.isSame(today.clone().subtract(1, "day"), "day")) {
    return "Yesterday";
  }
  return date.format("MMM D, YYYY");
};

/** Flatten events into table rows with full-width day group headers interleaved. */
export const buildAuditTableRows = (events: IdentityAuditEvent[]): AuditTableRow[] => {
  const rows: AuditTableRow[] = [];
  let currentLabel: string | null = null;

  events.forEach(event => {
    const label = formatGroupLabel(moment(event.created_at).local().startOf("day"));
    if (label !== currentLabel) {
      currentLabel = label;
      rows.push({
        id: `group-${label}-${rows.length}`,
        rowType: "groupHeader",
        label,
      });
    }
    rows.push({ ...event, rowType: "event" });
  });

  return rows;
};

const AUDIT_TABLE_HEADER_HEIGHT = 40;
const AUDIT_EVENT_ROW_HEIGHT = 48;
const AUDIT_GROUP_ROW_HEIGHT = 36;
const AUDIT_TABLE_MIN_HEIGHT = 120;
const AUDIT_TABLE_MAX_HEIGHT = 520;

/** Fit the grid to its rows so a fixed viewport does not leave an empty footer band. */
export const computeAuditTableHeight = (rows: AuditTableRow[]): string => {
  if (rows.length === 0) {
    return `${AUDIT_TABLE_MIN_HEIGHT}px`;
  }

  const bodyHeight = rows.reduce(
    (total, row) =>
      total + (row.rowType === "groupHeader" ? AUDIT_GROUP_ROW_HEIGHT : AUDIT_EVENT_ROW_HEIGHT),
    0,
  );

  const total = AUDIT_TABLE_HEADER_HEIGHT + bodyHeight + 2;
  return `${Math.min(AUDIT_TABLE_MAX_HEIGHT, Math.max(AUDIT_TABLE_MIN_HEIGHT, total))}px`;
};

export const countActiveAuditFilters = (filters: {
  event_types?: string[];
  source?: string;
  from?: string;
  to?: string;
}): number => {
  let count = filters.event_types?.length ?? 0;
  if (filters.source) {
    count += 1;
  }
  if (filters.from || filters.to) {
    count += 1;
  }
  return count;
};
